import { Box, Button } from "@mui/material";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { StagesRightSideContainer } from "./styles";
import { StagesLeftContainer, styles } from "./styles";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Stage } from "../../components/InitialSettingsComponents/Stage";
import { UserDetailsStage } from "../../components/InitialSettingsComponents/UserDetailsStage";
import { PersonalInformationStage } from "../../components/InitialSettingsComponents/PersonalInformationStage";
import { PreferencesStage } from "../../components/InitialSettingsComponents/PreferencesStage";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  registerInitialUser,
  registerMinorSubscription,
} from "../../services/registerUserService";
import { registerUser } from "../../services/registerUserService";
import { TEXT_COLOR, THEME } from "../../theme";
import { S3CredentialsContext } from "../../contexts";
import { AuthContext } from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import { useAuth0 } from "@auth0/auth0-react";
import { SettingsProps } from "./types";
import { useGetUser } from "../../hooks/useGetUser";
import { toast } from "react-toastify";
import Loading from "../Loading";
import {
  EnterpriseDetails,
  GetPendingEnterpriseInvitesResponse,
} from "../../proto/api_pb";
import { getPendingInvitation } from "../../services/enterpriseService";
import Mixpanel from "../../utils/mixpanel";

const InitialSettings: React.FC<SettingsProps> = ({ handleSuccess }) => {
  const [stage, setStage] = useState(0);
  const [stageName, setStageName] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [skills, setSkills] = useState<string[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [profilePicBlob, setProfilePicBlob] = useState<Blob | null>(null);
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [isStageNameEmpty, setIsStageNameEmpty] = useState<boolean>(false);
  const [isStageNameError, setIsStageNameError] = useState<boolean>(false);
  const [duplicateUsername, setDuplicateUsername] = useState<boolean>(false);
  const [isFirstNameError, setIsFirstNameError] = useState<boolean>(false);
  const [isLastNameError, setIsLastNameError] = useState<boolean>(false);
  const [pendingInvitation, setPendingInvitation] =
    useState<GetPendingEnterpriseInvitesResponse>();
  const validate = /^(\d|\w)+$/;
  const { userId } = useContext(AuthContext);
  const { getUserResponse, refreshUserDetails } = useGetUser(userId);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { user, logout } = useAuth0();

  const handleGetPendingInvites = useCallback(async () => {
    const tt = await getValidAccessToken();
    const pInvite = await getPendingInvitation(tt, user?.email ?? "");
    if (pInvite instanceof GetPendingEnterpriseInvitesResponse) {
      setPendingInvitation(pInvite);
    }
  }, [getValidAccessToken, user?.email]);

  useEffect(() => {
    handleGetPendingInvites();
    async function setupInitial() {
      const email = user?.email;
      if (email !== undefined && email.length > 0) {
        const token = await getValidAccessToken();
        await registerInitialUser(email, token);
      }
    }
    setupInitial();
    setEmail(user?.email || "");
  }, [user, getValidAccessToken, handleGetPendingInvites]);

  const handleStageChange = (newStage: number) => {
    setStage(newStage);
  };

  const handleUsernameChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setIsStageNameEmpty(false);
    setIsStageNameError(false);
    setDuplicateUsername(false);
    setStageName(e.target.value);
  };

  const handleChangeFirstName = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setIsFirstNameError(false);
    setFirstName(e.target.value);
  };

  const handleChangeLastName = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setIsLastNameError(false);
    setLastName(e.target.value);
  };

  const handleChangeProfilePic = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.currentTarget;
    if (files && files.length > 0) {
      const blob = new Blob([files[0]], { type: files[0].type });
      const url = URL.createObjectURL(blob);
      url && setProfilePicUrl(url);
      setProfilePicBlob(blob);
      setProfilePicFile(files[0]);
    }
  };

  const isFirstNameValid = () => {
    const trimmedValue = firstName.trim();
    if (firstName !== "") {
      if (!validate.test(trimmedValue)) {
        setIsFirstNameError(true);
        return false;
      }
    }
    setFirstName(trimmedValue);
    return true;
  };

  const isLastNameValid = () => {
    const trimmedValue = lastName.trim();
    if (lastName !== "")
      if (!validate.test(trimmedValue)) {
        setIsLastNameError(true);
        return false;
      }
    setLastName(trimmedValue);
    return true;
  };

  const isUserNameValid = () => {
    const trimmedValue = stageName.trim();
    if (stageName === "") {
      setIsStageNameEmpty(true);
      setStage(0);
      return false;
    } else if (!validate.test(trimmedValue)) {
      setIsStageNameError(true);
      setStage(0);
      return false;
    }
    setStageName(trimmedValue);
    return true;
  };

  const checkValidation = async (isFirstStep: boolean) => {
    if (!isFirstNameValid() || !isLastNameValid() || !isUserNameValid()) {
      isFirstNameValid();
      isLastNameValid();
      isUserNameValid();
      return false;
    } else {
      const isLastStep = false;
      const registrationSuccess = await handleRegisterUser(isLastStep, isFirstStep);
      if (registrationSuccess && isFirstStep) {
        setStage(1);
      }
      return true;
    }
  };

  const handleRegisterUser = async (isLastStep: boolean, isFirstStep: boolean = false): Promise<boolean> => {
    try {
      if (!isFirstNameValid() || !isLastNameValid() || !isUserNameValid()) {
        isFirstNameValid();
        isLastNameValid();
        isUserNameValid();
        return false; // Indicate failure
      }
      if (stageName) {
        setIsLoading(true);
        const accessToken = await getValidAccessToken();
        const profile = {
          firstName: firstName,
          lastName: lastName,
          username: stageName,
          email: email,
          bio: bio,
          connectedWallet: "",
          skills: skills,
          genres: genres,
          profilePic: profilePicUrl ?? "",
        };
        const allEnterprises =
          getUserResponse?.getEnterpriseDetailsList() ?? [];
        const ommuseExists = allEnterprises.some(
          (enterprise) => enterprise.getEnterpriseName() === "ommuse"
        );
        if (!ommuseExists) {
          const newEnterprise = new EnterpriseDetails();
          newEnterprise.setEnterpriseId("ommuse_enterprise_id");
          newEnterprise.setRole("artist");
          newEnterprise.setEnterpriseName("ommuse");
          allEnterprises.push(newEnterprise);
        }
        const pI = pendingInvitation?.getEnterpriseInvitationsList();
        if (pI && pI.length > 0) {
          pI.forEach((invite) => {
            const newEnterprise = new EnterpriseDetails();
            newEnterprise.setEnterpriseId(invite.getEnterpriseId());
            newEnterprise.setRole(invite.getRole());
            newEnterprise.setDateAdded(invite.getDateAdded());
            newEnterprise.setEnterpriseName(invite.getEnterpriseName());
            allEnterprises.push(newEnterprise);
          });
        }
        await registerUser(
          profile,
          profilePicBlob,
          userId,
          user,
          profilePicFile,
          accessToken,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          allEnterprises,
          0,
          0
        );
        if (isFirstStep) {
          await registerMinorSubscription(userId, accessToken);
        }
        if (isLastStep) {
          if (pI) {
            window.location.href = "/select-enterprise";
          } else {
            window.location.href = "/my-tracks?plan=minor";
          }
        }
        setIsLoading(false);
        handleSuccess();
        return true; // Indicate success
      }
      setIsLoading(false);
      refreshUserDetails();
      handleSuccess();
      return false; // Indicate failure if stageName is falsy
    } catch (error: any) {
      if (error instanceof RpcError) {
        setIsLoading(false);
        console.log(error.code, error.message);
        if (error.code === 6) {
          if (error.message.includes("username already exists")) {
            setDuplicateUsername(true);
            setStage(0);
          }
        } else {
          toast.warning("Error while registering user, please try again.");
        }
      }
      return false; // Indicate failure due to error
    }
  };

  useEffect(() => {
    if (getUserResponse) {
      setStageName(getUserResponse.getUsername());
      setFirstName(getUserResponse.getFirstName());
      setLastName(getUserResponse.getLastName());
      setBio(getUserResponse.getBio());
      setSkills(getUserResponse.getSkillsList());
      setGenres(getUserResponse.getGenresList());
    }
  }, [getUserResponse]);

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <StagesLeftContainer>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Stage
            icon={
              <Box
                sx={
                  stage === 0
                    ? {
                        ...styles.groupAddOutlinedIconContainer,
                        color: TEXT_COLOR,
                        borderColor: TEXT_COLOR,
                      }
                    : styles.groupAddOutlinedIconContainer
                }
              >
                <img
                  src="/assets/icons/InitialSettingsIcons/user-01.svg"
                  style={{
                    ...styles.groupAddOutlinedIcon,
                    filter:
                      stage === 0
                        ? THEME === "dark"
                          ? "invert(0%)"
                          : "invert(100%)"
                        : "invert(40%)",
                  }}
                  alt="icon"
                />
              </Box>
            }
            title="Your details"
            description="Provide your name and stage name"
            isActive={stage === 0}
            handleStageChange={handleStageChange}
            currentStage={0}
          />
          <Box sx={styles.verticalBar}></Box>
          <Stage
            icon={
              <Box
                sx={
                  stage === 1
                    ? {
                        ...styles.groupAddOutlinedIconContainer,
                        color: TEXT_COLOR,
                        borderColor: TEXT_COLOR,
                      }
                    : styles.groupAddOutlinedIconContainer
                }
              >
                <img
                  src="/assets/icons/InitialSettingsIcons/passcode.svg"
                  style={{
                    ...styles.groupAddOutlinedIcon,
                    filter:
                      stage === 1
                        ? THEME === "dark"
                          ? "invert(0%)"
                          : "invert(100%)"
                        : "invert(40%)",
                  }}
                  alt=""
                />
              </Box>
            }
            title="Personal information"
            description="Add a bio and photo"
            isActive={stage === 1}
            handleStageChange={handleStageChange}
            currentStage={1}
          />
          <Box sx={styles.verticalBar}></Box>
          <Stage
            icon={
              <Box
                sx={
                  stage === 2
                    ? {
                        ...styles.groupAddOutlinedIconContainer,
                        color: TEXT_COLOR,
                        borderColor: TEXT_COLOR,
                      }
                    : styles.groupAddOutlinedIconContainer
                }
              >
                <img
                  src="/assets/icons/InitialSettingsIcons/users-plus.svg"
                  style={{
                    ...styles.groupAddOutlinedIcon,
                    filter:
                      stage === 2
                        ? THEME === "dark"
                          ? "invert(100%)"
                          : "invert(0%)"
                        : "invert(40%)",
                  }}
                  alt="icon"
                />
              </Box>
            }
            title="Preferences"
            description="Add skills and genres"
            isActive={stage === 2}
            handleStageChange={handleStageChange}
            currentStage={2}
          />
        </Box>
      </StagesLeftContainer>
      <Box sx={styles.logoutMainContainer}>
        <Box sx={styles.logoutContainer}>
          <Button
            onClick={() => {
              logout({
                logoutParams: {
                  returnTo: window.location.origin + "/logout",
                },
              });
              sessionStorage.clear();
              localStorage.clear();
              Mixpanel.reset();
            }}
            sx={styles.logoutButton}
          >
            Logout
          </Button>
        </Box>
        <StagesRightSideContainer>
          {stage === 0 && (
            <UserDetailsStage
              email={user?.email}
              stageName={stageName}
              firstName={firstName}
              lastName={lastName}
              setStage={setStage}
              isStageNameEmpty={isStageNameEmpty}
              isStageNameError={isStageNameError}
              isFirstNameError={isFirstNameError}
              isLastNameError={isLastNameError}
              duplicateUsername={duplicateUsername}
              handleUsernameChange={handleUsernameChange}
              handleChangeFirstName={handleChangeFirstName}
              handleChangeLastName={handleChangeLastName}
              checkValidation={checkValidation}
              getUserResponse={getUserResponse}
              isLoading={isLoading}
            />
          )}
          {stage === 1 && (
            <PersonalInformationStage
              setStage={setStage}
              bio={bio}
              setBio={setBio}
              handleChangeProfilePic={handleChangeProfilePic}
              profilePicUrl={profilePicUrl}
              checkValidation={checkValidation}
              isLoading={isLoading}
            />
          )}
          {stage === 2 && (
            <PreferencesStage
              setSkills={setSkills}
              setGenres={setGenres}
              skills={skills}
              genres={genres}
              handleRegisterUser={handleRegisterUser}
              setStage={setStage}
              isLoading={isLoading}
            />
          )}
        </StagesRightSideContainer>
      </Box>
    </Box>
  );
};

export default withAuthenticationRequired(InitialSettings, {
  onRedirecting: () => <Loading />,
});